"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityTable = EntityTable;
exports.EntityTableWithDetails = EntityTableWithDetails;
const DataTable_1 = require("@juvare/common-web-ui/DataTable");
const DialogHelper_1 = require("@juvare/common-web-ui/DialogHelper");
const ModalSlideout_1 = require("@juvare/common-web-ui/ModalSlideout");
const SnackbarHelper_1 = require("@juvare/common-web-ui/SnackbarHelper");
const StorageClient_1 = require("@juvare/platform-sdk-js/StorageClient");
const entity_import_1 = require("../../../ui/features/entity-import");
const hooks_1 = require("../../../ui/hooks");
const material_1 = require("@mui/material");
const react_1 = __importDefault(require("react"));
const dynamicColumns_1 = require("./dynamicColumns");
const entityExport_1 = require("./entityExport");
/**
 * Deprecated. Use DataTable from common-data-table-ui together with useEntityDataTable hook.
 *
 * @deprecated
 */
function EntityTable(props) {
    const appliedFilters = react_1.default.useRef(undefined);
    const appliedSort = react_1.default.useRef(undefined);
    const exportClient = (0, hooks_1.useExportClient)();
    const { data: dynamicDefinitions } = (0, dynamicColumns_1.useDynamicColumnDefinitions)(props.entity, !!props.dynamicColumns);
    const filters = react_1.default.useMemo(() => {
        const finalFilters = [];
        if (props.filterGroups?.length) {
            const staticFilters = (props.filterGroups ?? []);
            finalFilters.push(...staticFilters);
        }
        if (props.dynamicColumns) {
            finalFilters.push(...(0, dynamicColumns_1.mapDynamicColumnsToFilterGroups)(dynamicDefinitions, props.dynamicColumnsOptions?.initialFilterValues));
        }
        return finalFilters.length ? finalFilters : undefined;
    }, [dynamicDefinitions, props.filterGroups, props.dynamicColumns, props.dynamicColumnsOptions?.initialFilterValues]);
    const handleFilterApplied = (filter, sort) => {
        if (props.onFilterApplied) {
            props.onFilterApplied(filter);
        }
        appliedSort.current = sort;
        appliedFilters.current = filter;
    };
    const service = react_1.default.useMemo(() => mapService(props, new StorageClient_1.StorageClient(props.entity), filters, handleFilterApplied), [props.entity, props.storageClientFilter, filters, props.fullTextSearchField]);
    // service to simulate always loading, used to display loading bar until dynamic columns are loaded from network
    const fakeService = react_1.default.useMemo(() => {
        return {
            id: (row) => row.id,
            list: () => new Promise(() => undefined),
        };
    }, []);
    const columns = react_1.default.useMemo(() => {
        const returnValue = props.columns.map((column) => ({
            name: column.name,
            label: column.label,
            sortable: column.sortable,
            width: column.width,
            render: column.render ?? ((row, column) => renderColumn(row, column)),
            sort: column.sort,
        }));
        if (props.dynamicColumns) {
            if (dynamicDefinitions?.columnDefs && dynamicDefinitions.handlerMap) {
                const dynamicColumns = dynamicDefinitions.columnDefs
                    .filter((columnDef) => columnDef.metadata?.showInTable)
                    .map((columnDef) => {
                    const handlerWithData = dynamicDefinitions.handlerMap.get(columnDef.metadata?.columnHandlerName ?? '');
                    return {
                        name: `_dynamic_${columnDef.name}`,
                        label: columnDef.metadata?.label,
                        render: (row) => (0, dynamicColumns_1.formatDynamicColumnValue)(row, columnDef, handlerWithData),
                    };
                });
                returnValue.push(...dynamicColumns);
            }
            else {
                return [
                    {
                        name: 'loading',
                        label: 'Loading...',
                    },
                ];
            }
        }
        return returnValue;
    }, [props.columns, props.dynamicColumns, dynamicDefinitions]);
    const mobileColumns = react_1.default.useMemo(() => {
        if (props.mobileColumns) {
            const returnValue = props.mobileColumns.map((column) => ({
                name: column.name,
                label: column.label,
                sortable: column.sortable,
                width: column.width,
                render: column.render ?? renderColumn,
                sort: column.sort,
            }));
            if (props.dynamicColumns) {
                if (dynamicDefinitions?.columnDefs && dynamicDefinitions.handlerMap) {
                    const dynamicColumns = dynamicDefinitions.columnDefs.filter((col) => col.metadata?.showInTable);
                    returnValue.push({
                        name: `_dynamic`,
                        label: 'Fields',
                        render: (row) => {
                            return dynamicColumns.map((columnDef) => (react_1.default.createElement("div", { key: columnDef.id },
                                react_1.default.createElement(material_1.Typography, { color: 'textSecondary', component: 'span' },
                                    columnDef.metadata?.label,
                                    ":\u00A0"),
                                (0, dynamicColumns_1.formatDynamicColumnValue)(row, columnDef, dynamicDefinitions.handlerMap.get(columnDef.metadata?.columnHandlerName ?? '')))));
                        },
                    });
                }
                else {
                    return [
                        {
                            name: 'loading',
                            label: 'Loading...',
                        },
                    ];
                }
            }
            return returnValue;
        }
        else {
            return undefined;
        }
    }, [props.mobileColumns, props.dynamicColumns, dynamicDefinitions]);
    const toolbarActions = [];
    if (props.toolbarActions) {
        toolbarActions.push(...props.toolbarActions);
    }
    if (props.export) {
        toolbarActions.push({
            label: props.exportOptions?.buttonLabel ?? 'Export',
            actionProps: {
                startIcon: props.exportOptions?.startIcon,
            },
            'data-test': 'export-button',
            onClick: () => {
                const onBeforeExport = props.exportOptions?.onBeforeExport || Promise.resolve.bind(Promise);
                onBeforeExport()
                    .then(() => (0, entityExport_1.beginExport)(exportClient, props.entity, props.exportOptions?.exportType || 'csv', props.multiSelectedRowIds, appliedFilters.current, appliedSort.current && mapSort(props, appliedSort.current)))
                    .catch(SnackbarHelper_1.handleErrorSnackbar);
            },
        });
    }
    if (props.import) {
        toolbarActions.push({
            label: props.importOptions?.buttonLabel ?? 'Import',
            actionProps: {
                startIcon: props.importOptions?.startIcon,
            },
            'data-test': 'import-button',
            onClick: () => {
                (0, entity_import_1.showEntityImportDialog)({
                    entity: props.entity,
                    options: props.importOptions,
                });
            },
        });
    }
    return (react_1.default.createElement(DataTable_1.DataTable, { service: !dynamicDefinitions ? fakeService : service, columns: columns, mobileColumns: mobileColumns, initialSortColumnName: props.initialSortColumnName, initialSortColumnDesc: props.initialSortColumnDesc, filterGroups: filters, fullTextSearchField: props.fullTextSearchField, fullTextSearchInitialValue: props.fullTextSearchInitialValue, showFiltersInitially: props.showFiltersInitially, showFullTextSearchChipLabel: props.showFullTextSearchChipLabel, groupsCollapsed: props.groupsCollapsed, filterActionsComponent: props.filterActionsComponent, toolbarComponent: props.toolbarComponent, toolbarActions: toolbarActions, rowMenuItems: props.rowMenuItems, reloadFlag: props.reloadFlag, selectedRowId: props.selectedRowId, onSelectRow: props.onSelectRow, multiSelect: props.multiSelect, multiSelectedRowIds: props.multiSelectedRowIds, onMultiSelectChange: props.onMultiSelectChange, "data-test": props['data-test'] }));
}
function mapService(props, storageClient, filterGroups, onFilterApplied) {
    return {
        id: (row) => row.id,
        list: (filter, sort, limit, offset) => {
            const entityFilter = mapFilter(props, filter, filterGroups);
            if (onFilterApplied) {
                onFilterApplied(entityFilter, sort);
            }
            return storageClient.list({
                filter: entityFilter,
                orderBy: mapSort(props, sort),
                orderByUpdatedDesc: sort.length > 0 ? sort[0].desc : false,
                limit: limit,
                offset: offset,
            });
        },
    };
}
function mapFilter(props, filter, filterGroups) {
    let userFilter = [];
    filter.forEach((f) => {
        if (props.fullTextSearchField === f.field) {
            userFilter.push({
                column: f.field,
                op: mapFilterOp(props, f),
                value: f.value,
            });
        }
        else if (filterGroups) {
            for (const propsFilterGroup of filterGroups) {
                for (const propsFilterConfig of propsFilterGroup.configs) {
                    if (propsFilterConfig.field === f.field) {
                        if (propsFilterConfig.mapToFilter) {
                            const mappedFilters = propsFilterConfig.mapToFilter(filter);
                            (mappedFilters ?? []).forEach((mappedFilter) => userFilter.push(mappedFilter));
                        }
                        else {
                            userFilter.push({
                                column: f.field,
                                op: mapFilterOp(props, f),
                                value: f.value,
                            });
                        }
                    }
                }
            }
        }
        return undefined;
    });
    userFilter = userFilter.filter((f) => !!f);
    if (props.storageClientFilter?.length) {
        // Filters from storageClientFilter must come first in this array.
        // Health Alerts rely on this implementation detail to implement group based filters.
        // Search for this code correlation id in health-alerts codebase to find the place:
        // code-correlation-id: e9f46c9f-bacc-49bc-9f59-37a0dcef5618
        return [...props.storageClientFilter, ...userFilter];
    }
    else {
        return userFilter;
    }
}
function mapFilterOp(props, filter) {
    if (filter.field === props.fullTextSearchField) {
        return 'full-text-query';
    }
    else if (filter.op) {
        return filter.op;
    }
    else if (Array.isArray(filter.value)) {
        return 'in';
    }
    else {
        return '=';
    }
}
function mapSort(props, sort) {
    let result = sort.map((s) => ({
        column: s.field,
        desc: s.desc,
    }));
    if (props.storageClientSort) {
        result = [...props.storageClientSort, ...result];
    }
    return result;
}
function renderColumn(row, column) {
    const value = row.columns[column.name];
    if (typeof value === 'boolean') {
        return react_1.default.createElement(react_1.default.Fragment, null, value ? 'true' : 'false');
    }
    else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return react_1.default.createElement(material_1.Typography, { noWrap: true }, value);
    }
}
function EntityTableWithDetails(props) {
    const { details, onCloseDetails, ...rest } = props;
    const storageClient = (0, hooks_1.useStorageClient)(props.entity);
    const loadSelectedRowData = (row) => {
        if (props.onSelectLoad) {
            props.onSelectLoad(row, () => {
                storageClient.get(row.id).then(props.onSelectData).catch(DialogHelper_1.handleErrorDialog);
            });
        }
    };
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(EntityTable, { ...rest, onSelectRow: loadSelectedRowData }),
        react_1.default.createElement(ModalSlideout_1.ModalSlideout, { open: !!details, onClose: () => onCloseDetails?.(), "data-test": 'entity-table-with-details-slideout' }, details)));
}
